import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, customerRequest } from '@/shared/api'
import {
  type CustomerProfile,
  CustomerProfileResponseSchema,
  customerProfileFrom,
} from '../model/types'
import { customerSessionKeys } from './queries'

export interface CustomerCredentials {
  login: string
  password: string
}

export interface PasswordChange {
  current: string
  next: string
}

// Минимальная длина нового пароля — контракт company-backend (customer.MinPasswordLen).
export const MIN_PASSWORD_LENGTH = 8

export function useCustomerLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (creds: CustomerCredentials): Promise<CustomerProfile> => {
      const body = await customerRequest<unknown>({
        method: 'POST',
        path: '/auth/customer/login',
        // Логин нормализуется на бэкенде (lower(trim)); пробелы по краям — почти
        // всегда артефакт копипасты, и снимать их до отправки дешевле, чем
        // объяснять «неверный логин».
        body: { login: creds.login.trim(), password: creds.password },
        noRefresh: true,
      })
      return customerProfileFrom(CustomerProfileResponseSchema.parse(body).data)
    },
    onSuccess: (profile) => {
      qc.setQueryData(customerSessionKeys.me, profile)
    },
  })
}

export function useCustomerLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      customerRequest<void>({ method: 'POST', path: '/auth/customer/logout', noRefresh: true }),
    // Cookie гасит сервер, но локальный кэш обнуляем в любом исходе: «вышел, но
    // экран показывает вошедшего» — худшее из состояний.
    onSettled: () => {
      qc.setQueryData(customerSessionKeys.me, null)
    },
  })
}

export function useChangeCustomerPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PasswordChange) =>
      customerRequest<void>({
        method: 'POST',
        path: '/auth/customer/password',
        body: { current_password: input.current, new_password: input.next },
      }),
    onError: (error) => {
      // 401 здесь двусмысленный: «текущий пароль не подошёл» ИЛИ «сессия умерла»
      // (ретрай по refresh уже был). Перепроверяем профиль — экран сам покажет,
      // что именно случилось.
      if (error instanceof ApiError && error.isUnauthorized) {
        void qc.invalidateQueries({ queryKey: customerSessionKeys.me })
      }
    },
  })
}
